import React, { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import DashboardIcon from '@mui/icons-material/Dashboard';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import PersonIcon from '@mui/icons-material/Person';
import ConstructionIcon from '@mui/icons-material/Construction';
import LabelIcon from '@mui/icons-material/Label';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { useAuth } from '../../contexts/AppContext';

const navItems = [
  { to: '/', label: 'Dashboard', icon: DashboardIcon, end: true },
  { to: '/incomes', label: 'Receitas', icon: TrendingUpIcon },
  { to: '/expenses', label: 'Despesas', icon: ReceiptLongIcon },
  { to: '/projects', label: 'Projetos', icon: ConstructionIcon },
  { to: '/owners', label: 'Credores', icon: PersonIcon },
  { to: '/categories', label: 'Categorias', icon: LabelIcon },
  { to: '/monthly', label: 'Mês', icon: CalendarMonthIcon },
  { to: '/compare', label: 'Comparar', icon: CompareArrowsIcon },
];

export function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [desktopExpanded, setDesktopExpanded] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const sidebarContent = (expanded: boolean, onToggle?: () => void) => (
    <>
      <div className="flex items-center justify-between border-b border-dark-border p-4">
        {expanded && (
          <div className="flex items-center gap-2">
            <AccountBalanceWalletIcon className="text-accent" />
            <span className="text-lg font-bold text-accent">MyBudget</span>
          </div>
        )}
        {onToggle && (
          <button
            type="button"
            onClick={onToggle}
            className="rounded-lg p-1 hover:bg-dark-border"
            aria-label={expanded ? 'Recolher menu' : 'Expandir menu'}
          >
            {expanded ? <ChevronLeftIcon fontSize="small" /> : <MenuIcon fontSize="small" />}
          </button>
        )}
        {!onToggle && (
          <button
            type="button"
            onClick={() => setMobileOpen(false)}
            className="rounded-lg p-1 hover:bg-dark-border md:hidden"
            aria-label="Fechar menu"
          >
            <CloseIcon fontSize="small" />
          </button>
        )}
      </div>
      <nav className="flex-1 overflow-y-auto p-2">
        {navItems.map(({ to, label, icon: Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            className={({ isActive }) =>
              `mb-1 flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors ${
                isActive
                  ? 'bg-accent/15 text-accent'
                  : 'text-gray-400 hover:bg-dark-border hover:text-gray-200'
              }`
            }
          >
            <Icon fontSize="small" />
            {expanded && <span>{label}</span>}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-dark-border p-4">
        <button
          type="button"
          onClick={handleLogout}
          className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-sm text-gray-400 hover:bg-dark-border hover:text-red-400"
        >
          <LogoutIcon fontSize="small" />
          {expanded && <span>Sair</span>}
        </button>
      </div>
    </>
  );

  return (
    <div className="flex min-h-screen bg-dark-bg text-gray-100">
      {/* Desktop sidebar */}
      <aside
        className={`${
          desktopExpanded ? 'w-64' : 'w-16'
        } hidden shrink-0 flex-col border-r border-dark-border bg-dark-surface/80 backdrop-blur transition-all md:flex`}
      >
        {sidebarContent(desktopExpanded, () => setDesktopExpanded((v) => !v))}
      </aside>

      {/* Mobile drawer */}
      <div
        className={`fixed inset-0 z-40 md:hidden ${mobileOpen ? 'pointer-events-auto' : 'pointer-events-none'}`}
        aria-hidden={!mobileOpen}
      >
        <button
          type="button"
          className={`absolute inset-0 bg-black/60 transition-opacity ${
            mobileOpen ? 'opacity-100' : 'opacity-0'
          }`}
          onClick={() => setMobileOpen(false)}
          aria-label="Fechar menu"
        />
        <aside
          className={`absolute inset-y-0 left-0 flex w-72 max-w-[85vw] flex-col border-r border-dark-border bg-dark-surface shadow-xl transition-transform duration-200 ${
            mobileOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          {sidebarContent(true)}
        </aside>
      </div>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="border-b border-dark-border bg-dark-surface/60 px-4 py-4 backdrop-blur sm:px-6 sm:py-5">
          <div className="flex items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              <button
                type="button"
                onClick={() => setMobileOpen(true)}
                className="rounded-lg p-1.5 hover:bg-dark-border md:hidden"
                aria-label="Abrir menu"
              >
                <MenuIcon fontSize="small" />
              </button>
              <div className="min-w-0">
                <h1 className="truncate text-lg font-semibold sm:text-xl">MyBudget</h1>
                <p className="truncate text-sm text-gray-400">Olá, {user?.name}</p>
              </div>
            </div>
            <div className="hidden shrink-0 rounded-full border border-accent/30 bg-accent/10 px-4 py-1.5 text-xs font-medium text-accent lg:block">
              Organize receitas, despesas e projeções
            </div>
          </div>
        </header>
        <main className="flex-1 overflow-auto bg-gradient-to-br from-dark-bg via-dark-bg to-accent-muted/20 p-4 sm:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
